import React, { useState, useEffect } from 'react';
import { Book, Network, Wifi, Shield, AlertCircle, CheckCircle, Copy, Info, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HybridModeGuide = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [radiusInfo, setRadiusInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRADIUSInfo();
  }, []);

  const fetchRADIUSInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/system/radius-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setRadiusInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching RADIUS info:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Tersalin ke clipboard!');
  };

  const CodeBlock = ({ code, language = 'bash' }) => (
    <div className="relative bg-gray-900 text-gray-100 rounded-lg p-4 my-4">
      <button
        onClick={() => copyToClipboard(code)}
        className="absolute top-2 right-2 p-2 hover:bg-gray-700 rounded transition-colors"
        title="Copy"
      >
        <Copy className="w-4 h-4" />
      </button>
      <pre className="overflow-x-auto text-sm">
        <code className={`language-${language}`}>{code}</code>
      </pre>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Book },
    { id: 'architecture', label: 'Arsitektur', icon: Network },
    { id: 'setup-public', label: 'Router IP Publik', icon: Shield },
    { id: 'setup-vpn', label: 'Router Tanpa IP', icon: Wifi },
    { id: 'best-practices', label: 'Best Practices', icon: CheckCircle }
  ];

  const radiusIp = radiusInfo?.radius_ip || 'YOUR_RADIUS_SERVER_IP';
  const radiusSecret = radiusInfo?.radius_secret || 'YOUR_RADIUS_SECRET';
  const vpnServer = radiusInfo?.vpn_server || 'YOUR_VPN_SERVER_IP';

  return (
    <div className="p-6 bg-gray-50 min-h-screen dark:bg-slate-900">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3 dark:text-slate-100">
              <Network className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              📚 Panduan Hybrid Mode
            </h1>
            <p className="text-gray-600 dark:text-slate-300">
              Setup campuran: <strong>RADIUS Only</strong> + <strong>RADIUS + VPN</strong> dalam satu sistem
            </p>
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <Card className="mb-6 border-l-4 border-l-purple-500 bg-purple-50 dark:bg-purple-900/30">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Info className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1 dark:text-purple-400" />
            <div>
              <h3 className="font-bold text-purple-900 mb-2">Mode Hybrid - Untuk Apa?</h3>
              <ul className="text-sm text-purple-800 space-y-1 list-disc list-inside">
                <li><strong>Punya beberapa router dengan IP Publik</strong> → gunakan RADIUS Only (lebih cepat & simple)</li>
                <li><strong>Punya beberapa router tanpa IP Publik</strong> → gunakan RADIUS + VPN (lebih aman)</li>
                <li><strong>Semua router bisa autentikasi ke RADIUS server yang sama</strong></li>
                <li><strong>Manajemen terpusat</strong> - semua customer dalam satu database</li>
              </ul>
              <p className="mt-3 text-sm text-purple-800 bg-white p-2 rounded border border-purple-200 dark:bg-slate-800">
                💡 <strong>Contoh Use Case:</strong> Anda punya 3 router di lokasi berbeda. Router A & B punya IP Publik (pakai RADIUS Only), Router C tidak punya IP Publik (pakai VPN). Semua tetap sinkron ke satu RADIUS server.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <Card>
        <CardContent className="pt-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Book className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  Apa itu Hybrid Mode?
                </h2>
                
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-6 dark:bg-purple-900/30">
                  <h3 className="font-bold text-purple-900 mb-3">Konsep Hybrid Mode:</h3>
                  <div className="space-y-3 text-sm text-purple-800">
                    <p>
                      Hybrid Mode adalah pendekatan <strong>fleksibel</strong> dimana Anda bisa mengkombinasikan dua metode koneksi:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      <div className="bg-white p-4 rounded-lg border border-green-200 dark:bg-slate-800">
                        <h4 className="font-bold text-green-800 mb-2 flex items-center gap-2">
                          <Shield className="w-5 h-5" />
                          RADIUS Only
                        </h4>
                        <ul className="text-xs text-green-700 space-y-1 list-disc list-inside dark:text-green-300">
                          <li>Router dengan IP Publik</li>
                          <li>Koneksi langsung ke RADIUS</li>
                          <li>Lebih cepat (no VPN overhead)</li>
                          <li>Setup lebih simple</li>
                        </ul>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-blue-200 dark:bg-slate-800">
                        <h4 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                          <Wifi className="w-5 h-5" />
                          RADIUS + VPN
                        </h4>
                        <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside dark:text-blue-300">
                          <li>Router tanpa IP Publik</li>
                          <li>Koneksi via L2TP VPN tunnel</li>
                          <li>Lebih aman (encrypted)</li>
                          <li>Bisa akses dari mana saja</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 dark:bg-slate-900 dark:border-slate-700">
                  <h3 className="font-bold text-gray-900 mb-3 dark:text-slate-100">Informasi RADIUS Server Anda:</h3>
                  {loading ? (
                    <p className="text-sm text-gray-600 dark:text-slate-300">Loading...</p>
                  ) : radiusInfo ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-32 dark:text-slate-300">RADIUS IP:</span>
                        <code className="bg-white px-3 py-1 rounded border text-sm font-mono dark:bg-slate-800">{radiusIp}</code>
                        <button
                          onClick={() => copyToClipboard(radiusIp)}
                          className="text-gray-500 hover:text-gray-700 p-1 dark:text-slate-400"
                          title="Copy"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-32 dark:text-slate-300">VPN Server:</span>
                        <code className="bg-white px-3 py-1 rounded border text-sm font-mono dark:bg-slate-800">{vpnServer}</code>
                        <button
                          onClick={() => copyToClipboard(vpnServer)}
                          className="text-gray-500 hover:text-gray-700 p-1 dark:text-slate-400"
                          title="Copy"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-32 dark:text-slate-300">RADIUS Secret:</span>
                        <code className="bg-white px-3 py-1 rounded border text-sm font-mono dark:bg-slate-800">{radiusSecret}</code>
                        <button
                          onClick={() => copyToClipboard(radiusSecret)}
                          className="text-gray-500 hover:text-gray-700 p-1 dark:text-slate-400"
                          title="Copy"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-red-600 dark:text-red-400">Gagal memuat info. Hubungi admin.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Architecture Tab */}
          {activeTab === 'architecture' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Network className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                Arsitektur Hybrid Mode
              </h2>

              <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
                <h3 className="font-bold text-purple-900 mb-4 text-center">Diagram Koneksi</h3>
                
                <div className="space-y-4 text-sm">
                  {/* Router dengan IP Publik */}
                  <div className="bg-white rounded-lg p-4 border-2 border-green-300 dark:bg-slate-800">
                    <div className="flex items-center gap-3 mb-2">
                      <Shield className="w-6 h-6 text-green-600 dark:text-green-400" />
                      <h4 className="font-bold text-green-800">Router A & B (IP Publik)</h4>
                    </div>
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                      <span>MikroTik Router</span>
                      <ArrowRight className="w-4 h-4" />
                      <span className="font-mono bg-green-100 px-2 py-1 rounded dark:bg-green-900/40">Direct Connection</span>
                      <ArrowRight className="w-4 h-4" />
                      <span>RADIUS Server ({radiusIp})</span>
                    </div>
                    <p className="text-xs text-green-600 mt-2 dark:text-green-400">✅ Port 1812/1813 dibuka di firewall</p>
                  </div>

                  {/* Router tanpa IP Publik */}
                  <div className="bg-white rounded-lg p-4 border-2 border-blue-300 dark:bg-slate-800">
                    <div className="flex items-center gap-3 mb-2">
                      <Wifi className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      <h4 className="font-bold text-blue-800">Router C (Tanpa IP Publik)</h4>
                    </div>
                    <div className="flex items-center gap-2 text-blue-700 flex-wrap dark:text-blue-300">
                      <span>MikroTik Router</span>
                      <ArrowRight className="w-4 h-4" />
                      <span className="font-mono bg-blue-100 px-2 py-1 rounded dark:bg-blue-900/40">L2TP VPN Tunnel</span>
                      <ArrowRight className="w-4 h-4" />
                      <span>VPN Server ({vpnServer})</span>
                      <ArrowRight className="w-4 h-4" />
                      <span>RADIUS Server</span>
                    </div>
                    <p className="text-xs text-blue-600 mt-2 dark:text-blue-400">✅ Akses via VPN - lebih aman</p>
                  </div>

                  {/* RADIUS Server */}
                  <div className="bg-purple-100 rounded-lg p-4 border-2 border-purple-400 dark:bg-purple-900/40">
                    <div className="text-center">
                      <h4 className="font-bold text-purple-900 mb-2">🎯 RADIUS Server (Pusat Autentikasi)</h4>
                      <p className="text-sm text-purple-700 dark:text-purple-300">
                        Semua router (A, B, C) autentikasi ke server yang sama<br/>
                        Database customer terpusat - mudah management
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <Card className="bg-yellow-50 border-yellow-200 dark:bg-yellow-900/30">
                <CardContent className="pt-4">
                  <div className="flex gap-3">
                    <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 dark:text-yellow-400" />
                    <div>
                      <h3 className="font-bold text-yellow-900 mb-2">⚠️ Penting Diperhatikan</h3>
                      <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                        <li>Setiap router harus didaftarkan di sistem (menu MPANEL)</li>
                        <li>RADIUS Secret harus sama untuk semua router</li>
                        <li>IP RADIUS berbeda: router IP Publik pakai IP eksternal, router VPN pakai IP internal VPN</li>
                        <li>Customer database tetap satu - bisa login dari router mana saja</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Setup Public IP Tab */}
          {activeTab === 'setup-public' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6 text-green-600 dark:text-green-400" />
                Setup Router dengan IP Publik
              </h2>

              <Card className="bg-green-50 border-green-200 dark:bg-green-900/30">
                <CardContent className="pt-4">
                  <p className="text-sm text-green-800">
                    Untuk router yang <strong>punya IP Publik statis</strong>, gunakan mode <strong>RADIUS Only</strong>.
                  </p>
                </CardContent>
              </Card>

              <div>
                <h3 className="font-bold text-lg mb-3">📋 Ikuti Panduan RADIUS Only</h3>
                <p className="text-sm text-gray-700 mb-4 dark:text-slate-200">
                  Setup lengkap untuk router dengan IP Publik sudah tersedia di dokumentasi terpisah:
                </p>

                <a 
                  href="/app/radius-only-guide" 
                  className="block"
                >
                  <Card className="border-green-300 bg-green-50 hover:bg-green-100 transition-colors cursor-pointer dark:bg-green-900/30 dark:hover:bg-green-900/40">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Shield className="w-8 h-8 text-green-600 dark:text-green-400" />
                          <div>
                            <h4 className="font-bold text-green-900">🟢 RADIUS Only Guide</h4>
                            <p className="text-sm text-green-700 dark:text-green-300">Setup lengkap untuk router dengan IP Publik</p>
                          </div>
                        </div>
                        <ArrowRight className="w-6 h-6 text-green-600 dark:text-green-400" />
                      </div>
                    </CardContent>
                  </Card>
                </a>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 dark:bg-slate-900 dark:border-slate-700">
                <h3 className="font-bold text-gray-900 mb-3 dark:text-slate-100">⚡ Quick Reference</h3>
                <div className="space-y-3 text-sm text-gray-700 dark:text-slate-200">
                  <div>
                    <p className="font-semibold mb-1">1. Tambah RADIUS Server:</p>
                    <CodeBlock code={`/radius add address=${radiusIp} secret=${radiusSecret} service=ppp,hotspot`} />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">2. Enable RADIUS di PPP:</p>
                    <CodeBlock code={`/ppp aaa set use-radius=yes`} />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">3. Enable RADIUS di Hotspot:</p>
                    <CodeBlock code={`/ip hotspot profile set [find] use-radius=yes`} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Setup VPN Tab */}
          {activeTab === 'setup-vpn' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Wifi className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                Setup Router Tanpa IP Publik (via VPN)
              </h2>

              <Card className="bg-blue-50 border-blue-200 dark:bg-blue-900/30">
                <CardContent className="pt-4">
                  <p className="text-sm text-blue-800">
                    Untuk router yang <strong>tidak punya IP Publik</strong>, gunakan mode <strong>RADIUS + VPN</strong> (L2TP).
                  </p>
                </CardContent>
              </Card>

              <div>
                <h3 className="font-bold text-lg mb-3">📋 Ikuti Panduan RADIUS + VPN</h3>
                <p className="text-sm text-gray-700 mb-4 dark:text-slate-200">
                  Setup lengkap untuk router tanpa IP Publik sudah tersedia di dokumentasi terpisah:
                </p>

                <div className="space-y-3">
                  <a 
                    href="/app/vpn-setup-guide" 
                    className="block"
                  >
                    <Card className="border-blue-300 bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer dark:bg-blue-900/30 dark:hover:bg-blue-900/40">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Wifi className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                            <div>
                              <h4 className="font-bold text-blue-900">🔐 VPN Setup Guide</h4>
                              <p className="text-sm text-blue-700 dark:text-blue-300">Setup L2TP VPN di MikroTik</p>
                            </div>
                          </div>
                          <ArrowRight className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                      </CardContent>
                    </Card>
                  </a>

                  <a 
                    href="/app/radius-guide" 
                    className="block"
                  >
                    <Card className="border-indigo-300 bg-indigo-50 hover:bg-indigo-100 transition-colors cursor-pointer dark:bg-indigo-900/30 dark:hover:bg-indigo-900/40">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Shield className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                            <div>
                              <h4 className="font-bold text-indigo-900">🔒 RADIUS + VPN Guide</h4>
                              <p className="text-sm text-indigo-700 dark:text-indigo-300">Setup RADIUS setelah VPN terhubung</p>
                            </div>
                          </div>
                          <ArrowRight className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                        </div>
                      </CardContent>
                    </Card>
                  </a>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 dark:bg-slate-900 dark:border-slate-700">
                <h3 className="font-bold text-gray-900 mb-3 dark:text-slate-100">⚡ Urutan Setup VPN Router</h3>
                <ol className="space-y-2 text-sm text-gray-700 list-decimal list-inside dark:text-slate-200">
                  <li><strong>Setup L2TP Client di MikroTik</strong> - koneksi ke VPN server</li>
                  <li><strong>Dapatkan IP VPN</strong> - router akan dapat IP dari VPN pool</li>
                  <li><strong>Tambah RADIUS Server</strong> - gunakan IP RADIUS yang bisa diakses via VPN</li>
                  <li><strong>Test Koneksi</strong> - ping ke RADIUS server via VPN tunnel</li>
                  <li><strong>Enable RADIUS</strong> - di PPP dan/atau Hotspot</li>
                </ol>
              </div>
            </div>
          )}

          {/* Best Practices Tab */}
          {activeTab === 'best-practices' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                Best Practices & Tips
              </h2>

              <Card className="bg-green-50 border-green-200 dark:bg-green-900/30">
                <CardContent className="pt-4">
                  <h3 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    ✅ Do's (Yang Harus Dilakukan)
                  </h3>
                  <ul className="space-y-2 text-sm text-green-800">
                    <li className="flex items-start gap-2">
                      <span className="font-bold">•</span>
                      <span><strong>Dokumentasikan setiap router</strong> - catat lokasi, mode koneksi (RADIUS Only / VPN), IP, dll</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">•</span>
                      <span><strong>Gunakan naming convention</strong> - misal: RTR-JAKARTA-PUB (IP Publik), RTR-BANDUNG-VPN (via VPN)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">•</span>
                      <span><strong>Monitor koneksi</strong> - setup monitoring untuk deteksi router offline</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">•</span>
                      <span><strong>Backup konfigurasi</strong> - export config MikroTik secara berkala</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">•</span>
                      <span><strong>Test authentication</strong> - pastikan customer bisa login dari semua router</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-red-50 border-red-200 dark:bg-red-900/30">
                <CardContent className="pt-4">
                  <h3 className="font-bold text-red-900 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    ❌ Don'ts (Yang Harus Dihindari)
                  </h3>
                  <ul className="space-y-2 text-sm text-red-800">
                    <li className="flex items-start gap-2">
                      <span className="font-bold">•</span>
                      <span><strong>Jangan gunakan RADIUS Secret berbeda</strong> - semua router harus pakai secret yang sama</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">•</span>
                      <span><strong>Jangan lupa daftarkan router di MPANEL</strong> - router harus terdaftar agar bisa monitoring</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">•</span>
                      <span><strong>Jangan expose RADIUS port (1812/1813) ke public</strong> untuk router VPN - gunakan VPN tunnel</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">•</span>
                      <span><strong>Jangan mix IP RADIUS</strong> - router IP Publik pakai IP eksternal, router VPN pakai IP internal</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-blue-50 border-blue-200 dark:bg-blue-900/30">
                <CardContent className="pt-4">
                  <h3 className="font-bold text-blue-900 mb-3">💡 Tips Troubleshooting</h3>
                  <div className="space-y-3 text-sm text-blue-800">
                    <div>
                      <p className="font-semibold mb-1">Router IP Publik tidak bisa autentikasi:</p>
                      <ul className="list-disc list-inside ml-4 space-y-1 text-xs">
                        <li>Cek firewall - pastikan port 1812/1813 terbuka</li>
                        <li>Test ping ke RADIUS IP: <code className="bg-white px-1 rounded dark:bg-slate-800">/ping {radiusIp}</code></li>
                        <li>Cek RADIUS Secret - harus match dengan server</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Router VPN tidak bisa autentikasi:</p>
                      <ul className="list-disc list-inside ml-4 space-y-1 text-xs">
                        <li>Cek VPN tunnel - pastikan status "connected"</li>
                        <li>Cek routing - pastikan bisa ping RADIUS via VPN</li>
                        <li>Cek IP RADIUS - harus pakai IP yang accessible via VPN</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-purple-50 border-purple-200 dark:bg-purple-900/30">
                <CardContent className="pt-4">
                  <h3 className="font-bold text-purple-900 mb-3">📊 Monitoring & Maintenance</h3>
                  <div className="space-y-2 text-sm text-purple-800">
                    <p><strong>Yang perlu dimonitor:</strong></p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Status VPN tunnel (untuk router VPN mode)</li>
                      <li>RADIUS authentication success/fail rate</li>
                      <li>Bandwidth usage per router</li>
                      <li>Online sessions per router</li>
                    </ul>
                    <p className="mt-3"><strong>Maintenance rutin:</strong></p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Update RouterOS secara berkala</li>
                      <li>Review logs untuk detect anomali</li>
                      <li>Test failover scenario (jika ada backup)</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HybridModeGuide;
